'use client';

import { Slot } from '@radix-ui/react-slot';
import { type MouseEvent, forwardRef, useCallback, useContext, useEffect, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledState } from '../../hooks/use-disabled-state';
import { useInertProps } from '../../hooks/use-inert-props';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { AdornedInputContext } from './context/adorned-input.context';
import { adornedInputAdornmentRecipe } from './styles/adorned-input-adornment.styles';
import type { AdornedInputAdornmentPropsInterface } from './types/adorned-input-adornment.types';

/**
 * @since 10.0.0
 */
export const AdornedInputAdornment = forwardRef<
  HTMLSpanElement,
  AdornedInputAdornmentPropsInterface
>((props, forwardedRef) => {
  const adornmentId = useId();

  const {
    asChild = false,
    children,
    onClick,
    className,
    css: cssProp,
    id = adornmentId,
    ...rest
  } = props;

  const { disabled } = useContext(FormFieldContext);
  const { setAdornmentId, size } = useContext(AdornedInputContext);

  const styles = css(adornedInputAdornmentRecipe.raw({ disabled, size }), cssProp);

  useEffect(() => {
    if (!id) return;

    setAdornmentId?.(id);
  }, [id, setAdornmentId]);

  const handleAdornmentClick = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      if (disabled) return;

      const target = event.currentTarget as HTMLElement;
      const isInteractive = target.matches('button, a, [role="button"]');

      if (!isInteractive) {
        const input = target.parentElement?.querySelector('input');
        input?.focus();
      }

      onClick?.(event);
    },
    [disabled, onClick],
  );

  const a11yDisabledState = useDisabledState({
    disabled: asChild && disabled,
  });

  const inertProps = useInertProps(disabled && !asChild);

  const Component = asChild ? Slot : 'span';

  return (
    <Component
      ref={forwardedRef}
      className={cx('c11n', styles, className)}
      data-c11n-component="AdornedInput.Adornment"
      data-c11n-version={__C11N_VERSION__}
      onClick={handleAdornmentClick}
      id={id}
      {...inertProps}
      {...rest}
      {...a11yDisabledState}
    >
      {children}
    </Component>
  );
});

AdornedInputAdornment.displayName = 'AdornedInput.Adornment';
